import createClient from "openapi-fetch";
import type { paths, components } from "@trafficplace/api-client";

import { getInitData } from "../../../lib/tg";

// Клиент openapi-fetch с авторизацией через Telegram initData
const client = createClient<paths>({
  baseUrl: import.meta.env.VITE_API_URL,
});

// Inline-тип для создания заявки на вывод (T35).
// TODO(Phase 2c): перевести finance endpoints на typed openapi-fetch клиент
// (после T53 типы доступны в schema.ts, но safeFetch + page-level тесты
// требуют отдельного слайса для миграции).
type CreateWithdrawalIn = {
  amount: string;
  wallet_address: string;
  method?: "crypto_check" | "crypto_transfer" | "support";
};

type PendingWithdrawalRequest = {
  fingerprint: string;
  idempotencyKey: string;
  createdAt: number;
};

const WITHDRAWAL_REQUEST_STORAGE_KEY =
  "trafficplace:withdrawal-request-idempotency:v1";
const WITHDRAWAL_REQUEST_MAX_AGE_MS = 24 * 60 * 60 * 1000;
let pendingWithdrawalRequest: PendingWithdrawalRequest | null = null;

function withdrawalRequestFingerprint(body: CreateWithdrawalIn): string {
  return JSON.stringify({
    amount: body.amount,
    method: body.method ?? "crypto_transfer",
    wallet_address: body.wallet_address,
  });
}

function createIdempotencyKey(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  if (typeof globalThis.crypto?.getRandomValues !== "function") {
    throw new Error("secure random generator is unavailable");
  }
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

function readStoredWithdrawalRequest(): PendingWithdrawalRequest | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(
      WITHDRAWAL_REQUEST_STORAGE_KEY,
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingWithdrawalRequest>;
    if (
      typeof parsed.fingerprint !== "string" ||
      typeof parsed.idempotencyKey !== "string" ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        parsed.idempotencyKey,
      ) ||
      typeof parsed.createdAt !== "number" ||
      Date.now() - parsed.createdAt > WITHDRAWAL_REQUEST_MAX_AGE_MS
    ) {
      globalThis.sessionStorage?.removeItem(WITHDRAWAL_REQUEST_STORAGE_KEY);
      return null;
    }
    return parsed as PendingWithdrawalRequest;
  } catch {
    return null;
  }
}

function idempotencyKeyForWithdrawal(body: CreateWithdrawalIn): string {
  const fingerprint = withdrawalRequestFingerprint(body);
  const stored = pendingWithdrawalRequest ?? readStoredWithdrawalRequest();
  if (stored?.fingerprint === fingerprint) {
    pendingWithdrawalRequest = stored;
    return stored.idempotencyKey;
  }
  const created: PendingWithdrawalRequest = {
    fingerprint,
    idempotencyKey: createIdempotencyKey(),
    createdAt: Date.now(),
  };
  pendingWithdrawalRequest = created;
  try {
    globalThis.sessionStorage?.setItem(
      WITHDRAWAL_REQUEST_STORAGE_KEY,
      JSON.stringify(created),
    );
  } catch {
    // Module memory still keeps the key stable for the current Mini App run.
  }
  return created.idempotencyKey;
}

function clearWithdrawalIdempotencyKey(idempotencyKey: string): void {
  if (pendingWithdrawalRequest?.idempotencyKey === idempotencyKey) {
    pendingWithdrawalRequest = null;
  }
  try {
    const stored = readStoredWithdrawalRequest();
    if (stored?.idempotencyKey === idempotencyKey) {
      globalThis.sessionStorage?.removeItem(WITHDRAWAL_REQUEST_STORAGE_KEY);
    }
  } catch {
    // Storage availability is optional; the successful request is complete.
  }
}

export type OwnResourceType =
  | "channel_public"
  | "channel_private"
  | "chat_public"
  | "chat_private"
  | "bot";

export type OwnResourcePatch = {
  name?: string;
  type?: OwnResourceType;
  url?: string;
  target_action?: "subscribe" | "join_request" | "join_confirmed" | "start_bot";
  bot_launch_mode?: "start" | "unique_start";
  verification_mode?: "verified" | "no_check";
  botstat_code?: string | null;
  botstat_bot_username?: string | null;
  bot_token?: string;
};

export type FlowDesign = components["schemas"]["FlowDesignOut"];
export type FlowDesignUpdate = components["schemas"]["FlowDesignUpdateIn"];
export type FlowDesignSendResult = components["schemas"]["FlowDesignSendOut"];

type OwnResourceSettingsPatch = {
  enabled?: boolean;
};

// Вспомогательная функция для формирования заголовка авторизации
function authHeaders(): { Authorization: string } {
  return { Authorization: `tma ${getInitData()}` };
}

// Вспомогательная функция: единообразная обработка r.ok + parse error.
// Без неё 401/500 попадают в data и UI silently breaks (Phase 1b smoke lesson).
async function safeFetch<T>(
  input: RequestInfo,
  init?: RequestInit,
): Promise<{ data: T | null; error: unknown; status?: number }> {
  const r = await fetch(input, init);
  if (r.status === 204) {
    return r.ok
      ? { data: null, error: undefined, status: r.status }
      : {
          data: null,
          error: { detail: "empty error response" },
          status: r.status,
        };
  }
  try {
    const data = await r.json();
    return r.ok
      ? { data: data as T, error: undefined, status: r.status }
      : { data: null, error: data, status: r.status };
  } catch {
    return { data: null, error: { detail: "parse error" }, status: r.status };
  }
}

export type DriverWallet = components["schemas"]["DriverWalletOut"];
export type ConversionPreview = components["schemas"]["ConversionPreviewOut"];
export type DriverStatsSnapshot = components["schemas"]["DriverStatsOut"];

// Отдельные ключи для нового USDT-контракта и обмена: старый RUB-запрос
// никогда не может стать запросом в другой валюте после обновления Mini App.
const walletRequestKeys = new Map<string, string>();
async function walletMutation(path: string, body: Record<string, unknown>) {
  const storageKey = `trafficplace:driver-wallet:v1:${path}:${JSON.stringify(body)}`;
  let key = walletRequestKeys.get(storageKey);
  try {
    key ??= sessionStorage.getItem(storageKey) ?? undefined;
  } catch {
    /* Private storage may be unavailable. */
  }
  key ??= createIdempotencyKey();
  walletRequestKeys.set(storageKey, key);
  try {
    sessionStorage.setItem(storageKey, key);
  } catch {
    /* In-memory retry remains available. */
  }
  const result = await safeFetch(
    `${import.meta.env.VITE_API_URL}/finance/${path}`,
    {
      method: "POST",
      headers: {
        ...authHeaders(),
        "Content-Type": "application/json",
        "Idempotency-Key": key,
      },
      body: JSON.stringify(body),
    },
  );
  if (!result.error) {
    walletRequestKeys.delete(storageKey);
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      /* Best effort. */
    }
  }
  return result;
}

export const driverApi = {
  getWallet: () =>
    safeFetch<DriverWallet>(
      `${import.meta.env.VITE_API_URL}/finance/driver-wallet`,
      { headers: authHeaders() },
    ),
  previewConversion: (amount_rub: string) =>
    safeFetch<ConversionPreview>(
      `${import.meta.env.VITE_API_URL}/finance/conversions/preview`,
      {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ amount_rub }),
      },
    ),
  convert: (body: { amount_rub: string; confirmed_rate_id: number }) =>
    walletMutation("conversions", body),
  createUsdtWithdrawal: (body: {
    amount_usdt: string;
    method: "crypto_check" | "support";
    wallet_address: string;
  }) => walletMutation("usdt-withdrawals", body),
  listFlows: () => client.GET("/flows", { headers: authHeaders() }),

  getFlow: (id: number) =>
    client.GET("/flows/{flow_id}", {
      params: { path: { flow_id: id } },
      headers: authHeaders(),
    }),

  createFlow: (body: components["schemas"]["CreateFlowIn"]) =>
    client.POST("/flows", { body, headers: authHeaders() }),

  patchFlow: (id: number, body: components["schemas"]["UpdateFlowIn"]) =>
    client.PATCH("/flows/{flow_id}", {
      params: { path: { flow_id: id } },
      body,
      headers: authHeaders(),
    }),

  deleteFlow: (id: number) =>
    client.DELETE("/flows/{flow_id}", {
      params: { path: { flow_id: id } },
      headers: authHeaders(),
    }),

  getFlowDesign: (id: number) =>
    client.GET("/flows/{flow_id}/design", {
      params: { path: { flow_id: id } },
      headers: authHeaders(),
    }),

  updateFlowDesign: (id: number, body: FlowDesignUpdate) =>
    client.PUT("/flows/{flow_id}/design", {
      params: { path: { flow_id: id } },
      body,
      headers: authHeaders(),
    }),

  deleteFlowDesign: (id: number, revision?: number) =>
    client.DELETE("/flows/{flow_id}/design", {
      params: { path: { flow_id: id }, query: { revision } },
      headers: authHeaders(),
    }),

  sendFlowDesign: (id: number) =>
    client.POST("/flows/{flow_id}/design/send", {
      params: { path: { flow_id: id } },
      headers: authHeaders(),
    }),

  // Finance endpoints (T35-T38). Типы есть в schema.ts (T53),
  // но миграция safeFetch -> typed client отложена в Phase 2c.
  getBalance: (): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/finance/balance/driver`, {
      headers: authHeaders(),
    }),

  getDriverApiKey: (): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/driver-api/v1/key`, {
      headers: authHeaders(),
    }),

  listWithdrawals: (): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/finance/withdrawals`, {
      headers: authHeaders(),
    }),

  listOperations: (params?: {
    limit?: number;
    offset?: number;
  }): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/finance/operations/driver${params ? `?limit=${params.limit ?? 50}&offset=${params.offset ?? 0}` : ""}`,
      {
        headers: authHeaders(),
      },
    ),

  getWithdrawalRate: (): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/finance/withdrawal-rate`, {
      headers: authHeaders(),
    }),

  createWithdrawal: async (
    body: CreateWithdrawalIn,
  ): Promise<{ data: unknown; error: unknown }> => {
    const idempotencyKey = idempotencyKeyForWithdrawal(body);
    const result = await safeFetch(
      `${import.meta.env.VITE_API_URL}/finance/withdrawals`,
      {
        method: "POST",
        headers: {
          ...authHeaders(),
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify(body),
      },
    );
    if (!result.error) {
      clearWithdrawalIdempotencyKey(idempotencyKey);
    }
    return result;
  },

  cancelWithdrawal: (id: number): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/finance/withdrawals/${id}/cancel`,
      {
        method: "POST",
        headers: authHeaders(),
      },
    ),

  getStats: (params?: {
    flowId?: number;
    dateFrom?: string;
    dateTo?: string;
    refresh?: boolean;
  }): Promise<{
    data: DriverStatsSnapshot | null;
    error: unknown;
    status?: number;
  }> =>
    safeFetch<DriverStatsSnapshot>(
      `${import.meta.env.VITE_API_URL}/driver/stats${statsQuery(params)}`,
      {
        headers: authHeaders(),
      },
    ),

  createStatsExportLink: (): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/driver/stats/export-link`, {
      method: "POST",
      headers: authHeaders(),
    }),

  sendStatsExport: (params?: {
    flowId?: number;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/driver/stats/export-send${statsQuery(params)}`,
      {
        method: "POST",
        headers: authHeaders(),
      },
    ),

  exportStatsUrl: (): string =>
    `${import.meta.env.VITE_API_URL}/driver/stats/export.csv`,

  listResourceOptions: (
    category: string,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/resource-options?category=${encodeURIComponent(category)}`,
      {
        headers: authHeaders(),
      },
    ),

  getResourceSelectionRules: (
    category: string,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/resource-selection-rules?category=${encodeURIComponent(category)}`,
      {
        headers: authHeaders(),
      },
    ),

  getOwnResourceRules: (
    signal?: AbortSignal,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/flows/own-resource-rules`, {
      headers: authHeaders(),
      signal,
    }),

  listOwnResources: (
    options?: { selectableOnly?: boolean },
    signal?: AbortSignal,
  ): Promise<{ data: unknown; error: unknown }> => {
    const query = options?.selectableOnly ? "?selectable_only=true" : "";
    return safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/own-resources${query}`,
      {
        headers: authHeaders(),
        signal,
      },
    );
  },

  getOwnResource: (
    id: number,
    signal?: AbortSignal,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/flows/own-resources/${id}`, {
      headers: authHeaders(),
      signal,
    }),

  createOwnResource: (body: {
    name: string;
    type: OwnResourceType;
  }): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/flows/own-resources`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  patchOwnResource: (
    id: number,
    body: OwnResourcePatch,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/flows/own-resources/${id}`, {
      method: "PATCH",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  patchOwnResourceSettings: (
    id: number,
    body: OwnResourceSettingsPatch,
  ): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/own-resources/${id}/settings`,
      {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    ),

  deleteOwnResource: (id: number): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(`${import.meta.env.VITE_API_URL}/flows/own-resources/${id}`, {
      method: "DELETE",
      headers: authHeaders(),
    }),

  verifyOwnResource: (id: number): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/own-resources/${id}/verify`,
      {
        method: "POST",
        headers: authHeaders(),
      },
    ),

  submitOwnResource: (id: number): Promise<{ data: unknown; error: unknown }> =>
    safeFetch(
      `${import.meta.env.VITE_API_URL}/flows/own-resources/${id}/submit`,
      {
        method: "POST",
        headers: authHeaders(),
      },
    ),
};

function statsQuery(params?: {
  flowId?: number;
  dateFrom?: string;
  dateTo?: string;
  refresh?: boolean;
}): string {
  if (!params) return "";
  const query = new URLSearchParams();
  if (params.flowId) query.set("flow_id", String(params.flowId));
  if (params.dateFrom) query.set("date_from", params.dateFrom);
  if (params.dateTo) query.set("date_to", params.dateTo);
  if (params.refresh) query.set("refresh", "true");
  const raw = query.toString();
  return raw ? `?${raw}` : "";
}
